import { GitBranch } from "lucide-react";
import type { RepoInfo } from "../../types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useIssuesMeta } from "./meta";

/** Sentinel for "the default repo"; pickers never send it to the API. */
export const DEFAULT_REPO = "__default__";

export function useRepos(): RepoInfo[] {
  return useIssuesMeta().data?.repos ?? [];
}

/** The repo behind a key, for a label or a URL. */
export function useRepo(key: string | null | undefined): RepoInfo | undefined {
  const repos = useRepos();
  return key ? repos.find((repo) => repo.key === key) : undefined;
}

/** `github.com/leaselab/monorepo` for a GitHub URL, else the host and path. */
export function shortRepoUrl(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\.git$/, "");
}

/**
 * Picks one of the active repos. `allowDefault` adds a first entry for the
 * default repo (the sentinel `DEFAULT_REPO`), for forms where leaving the
 * choice to Settings is the normal case.
 */
export function RepoSelect({
  value,
  onChange,
  allowDefault = false,
  disabled,
  className,
  id,
  ariaLabel,
}: {
  value: string;
  onChange: (key: string) => void;
  allowDefault?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  ariaLabel?: string;
}) {
  const repos = useRepos();
  const active = repos.filter((repo) => repo.active || repo.key === value);
  const fallback = repos.find((repo) => repo.isDefault);
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={className} aria-label={ariaLabel ?? "Repository"}>
        <SelectValue placeholder="Repository" />
      </SelectTrigger>
      <SelectContent>
        {allowDefault && (
          <SelectItem value={DEFAULT_REPO}>
            <GitBranch className="size-3.5 text-muted-foreground" />
            Default{fallback ? ` (${fallback.name})` : ""}
          </SelectItem>
        )}
        {active.map((repo) => (
          <SelectItem key={repo.key} value={repo.key}>
            <GitBranch className="size-3.5 text-muted-foreground" />
            {repo.name}
            <span className="font-mono text-xs text-muted-foreground">{repo.key}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
